import { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import api from '../../../lib/api';
import { buildingRoomDisplay } from '../../../lib/formatUtils';
import {
  groupAssetsByType,
  filterExitEmergencyAssets,
  filterDbRcdAssets,
  filterFanHeaterAssets,
  filterBySingleAssetType,
} from '../../../lib/sessionGroupingUtils';

/**
 * Loads, paginates, offline-preloads, and groups the assets for an active test
 * session.
 *
 * Owns the equipment-code search + sort controls, the paged
 * `GET /assets/site/:id` query, the page-reset / page-merge / auto-preload
 * effects, and the element-type filtering + grouping/sorting memos. Extracted
 * from TestSessionView so the orchestrator no longer owns asset-loading
 * plumbing. Behaviour is unchanged from the inline versions.
 *
 * The caller keeps ownership of UI-selection state (which group is selected /
 * expanded); this hook only answers "which assets, loaded and grouped".
 *
 * @param {object} params
 * @param {string|number|null} params.siteId
 * @param {boolean} params.jobStarted
 * @param {string} params.sessionAssetType
 * @param {string} params.sessionElementType
 * @param {number} [params.refreshNonce] - bump to force a page-1 reset + refetch
 *   (e.g. after creating an asset), so a new asset that sorts onto an earlier
 *   page than the current auto-preload cursor still appears.
 */
export function useSessionAssets({ siteId, jobStarted, sessionAssetType, sessionElementType, refreshNonce = 0 }) {
  const [equipmentCodeSearch, setEquipmentCodeSearch] = useState('');
  const [debouncedEquipmentCodeSearch, setDebouncedEquipmentCodeSearch] = useState('');
  const [sessionAssetsPage, setSessionAssetsPage] = useState(1);
  const [sessionAssetsList, setSessionAssetsList] = useState([]);
  const [sessionSortBy, setSessionSortBy] = useState('equipment_code');
  const [sessionSortOrder, setSessionSortOrder] = useState('asc');

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedEquipmentCodeSearch(equipmentCodeSearch.trim());
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [equipmentCodeSearch]);

  // Fetch paged site assets for current search term. Asset type filtering remains client-side.
  const {
    data: assetsPayload,
    isLoading: assetsLoading,
    isFetching: assetsFetching,
    error: _assetsError,
  } = useQuery(
    ['sessionAssets', siteId, debouncedEquipmentCodeSearch, sessionAssetsPage, sessionSortBy, sessionSortOrder],
    () => api.get(`/assets/site/${siteId}`, {
      params: {
        search: debouncedEquipmentCodeSearch || undefined,
        page: sessionAssetsPage,
        limit: 100,
        sortBy: sessionSortBy,
        sortOrder: sessionSortOrder,
        // Inactive/removed assets (condition rating 5/6) can't be saved by
        // POST /tests/batch (requires status = 'active'), so keep them out of
        // the session list entirely rather than showing Pass/Fail buttons that
        // fail on tap.
        activeOnly: true,
      },
    }).then((res) => res.data),
    {
      enabled: !!siteId && jobStarted,
      keepPreviousData: true,
    }
  );

  const assetsPageItems = assetsPayload?.assets ?? (Array.isArray(assetsPayload) ? assetsPayload : []);
  const assetsPagination = assetsPayload?.pagination ?? {
    totalCount: assetsPageItems.length,
    totalPages: assetsPageItems.length > 0 ? 1 : 0,
    currentPage: 1,
    hasNextPage: false,
  };

  // Sanitised page count: a finite positive integer, else null. Used to bound
  // the offline auto-preload loop so malformed pagination can't loop forever.
  const rawTotalPages = Number(assetsPagination?.totalPages);
  const safeTotalPages = Number.isFinite(rawTotalPages) && rawTotalPages > 0
    ? Math.floor(rawTotalPages)
    : null;

  useEffect(() => {
    if (!siteId || !jobStarted) {
      setSessionAssetsList([]);
      setSessionAssetsPage(1);
      return;
    }
    setSessionAssetsPage(1);
    setSessionAssetsList([]);
  }, [siteId, jobStarted, debouncedEquipmentCodeSearch, sessionSortBy, sessionSortOrder, refreshNonce]);

  useEffect(() => {
    if (!jobStarted || !siteId) return;
    setSessionAssetsList((prev) => {
      if (sessionAssetsPage === 1) return assetsPageItems;
      const merged = new Map(prev.map((asset) => [asset.id, asset]));
      assetsPageItems.forEach((asset) => merged.set(asset.id, asset));
      return Array.from(merged.values());
    });
  }, [assetsPageItems, sessionAssetsPage, siteId, jobStarted]);

  // Auto-preload all pages for offline resilience (only when not searching).
  useEffect(() => {
    if (!jobStarted || !siteId) return;
    if (debouncedEquipmentCodeSearch) return;
    if (assetsLoading || assetsFetching) return;
    // Sanity guard: bail if pagination metadata is malformed (missing/NaN/<=0
    // totalPages) or every page is already loaded. Without this a buggy
    // hasNextPage flag would increment the page forever (infinite refetch loop).
    if (safeTotalPages == null) return;
    if (sessionAssetsPage >= safeTotalPages) return;
    if (!assetsPagination?.hasNextPage) return;
    setSessionAssetsPage((p) => p + 1);
  }, [
    jobStarted,
    siteId,
    debouncedEquipmentCodeSearch,
    assetsLoading,
    assetsFetching,
    assetsPagination?.hasNextPage,
    safeTotalPages,
    sessionAssetsPage,
  ]);

  const rawSessionAssets = sessionAssetsList;
  const autoPreloadEnabled = !!siteId && jobStarted && !debouncedEquipmentCodeSearch;
  const autoPreloadTotal = assetsPagination?.totalCount ?? rawSessionAssets.length;
  const autoPreloadLoaded = rawSessionAssets.length;
  const autoPreloading = autoPreloadEnabled && (assetsLoading || assetsFetching || assetsPagination?.hasNextPage);
  const autoPreloadPercent = autoPreloadEnabled && autoPreloadTotal > 0
    ? Math.min(100, Math.floor((autoPreloadLoaded / autoPreloadTotal) * 100))
    : 0;

  const sessionAssets = useMemo(() => {
    if (!sessionAssetType) return [];
    const effectiveElementType = sessionElementType || 'electrical';
    const byElement = rawSessionAssets.filter((a) => {
      const et = (a.element_type || 'electrical').trim().toLowerCase();
      return et === effectiveElementType.trim().toLowerCase();
    });
    if (sessionAssetType === 'combined_exit_emergency') return filterExitEmergencyAssets(byElement);
    if (sessionAssetType === 'combined_db_rcd') return filterDbRcdAssets(byElement);
    if (sessionAssetType === 'combined_fan_heater') return filterFanHeaterAssets(byElement);
    return filterBySingleAssetType(byElement, sessionAssetType);
  }, [rawSessionAssets, sessionAssetType, sessionElementType]);

  const groupedAssets = useMemo(() => {
    const grouped = groupAssetsByType(
      sessionAssets,
      sessionAssetType === 'combined_exit_emergency' ? 'exit_light' : sessionAssetType
    );
    const isDesc = (sessionSortOrder || 'asc').toLowerCase() === 'desc';
    const mult = isDesc ? -1 : 1;
    const compareGroupNames = (a, b) => mult * (a || '').localeCompare(b || '', undefined, { numeric: true });
    const byLocation = (sessionSortBy || 'location') === 'location';
    const withinGroupCompare = (a, b) => {
      if (byLocation) {
        const locA = buildingRoomDisplay(a.building, a.room);
        const locB = buildingRoomDisplay(b.building, b.room);
        const cmp = locA.localeCompare(locB, undefined, { numeric: true });
        if (cmp !== 0) return mult * cmp;
      }
      const codeA = (a.equipment_code || a.asset_code || '').trim();
      const codeB = (b.equipment_code || b.asset_code || '').trim();
      return mult * codeA.localeCompare(codeB, undefined, { numeric: true });
    };
    const groupsSorted = [...grouped].sort((ga, gb) => compareGroupNames(ga.groupName, gb.groupName));
    return groupsSorted.map(({ groupName, items }) => ({
      groupName,
      items: [...items].sort(withinGroupCompare),
    }));
  }, [sessionAssets, sessionAssetType, sessionSortBy, sessionSortOrder]);

  return {
    equipmentCodeSearch,
    setEquipmentCodeSearch,
    debouncedEquipmentCodeSearch,
    sessionSortBy,
    setSessionSortBy,
    sessionSortOrder,
    setSessionSortOrder,
    sessionAssetsPage,
    setSessionAssetsList,
    assetsLoading,
    groupedAssets,
    rawSessionAssets,
    autoPreloadEnabled,
    autoPreloading,
    autoPreloadLoaded,
    autoPreloadTotal,
    autoPreloadPercent,
  };
}
